import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CircleDot, Play, Square } from 'lucide-react'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { listMeetingRecordings, meetingPlayback, setMeetingRecording } from './api'
import type { MeetingRecording } from './types'

const POLL_MS = 8000

/**
 * What was kept.
 *
 * ## One file per person, and the panel says so
 *
 * LiveKit can record the room as one composite, and it does that by launching headless
 * Chrome against a layout page fetched over the internet — which is how live coverage
 * recorded nothing for a month. Per-participant files need no browser, and they are the
 * better raw material for the transcript that comes next: one file per speaker is
 * attribution we already have, where a composite would need diarisation to recover it.
 *
 * ## PENDING is not "recording"
 *
 * The state shown is the state the server holds, and the distinction is deliberate: the
 * media server accepting a job is not the same as its pipeline writing frames, and a panel
 * that showed "Recording" for an accepted-but-stalled job would be lying in the direction
 * that costs somebody their meeting.
 */
export function ArchivePanel({
  meetingId, recordingEnabled, live, canHost, onToggled,
}: {
  meetingId: string
  recordingEnabled: boolean
  /** The meeting is LIVE — see the note below for why the distinction matters. */
  live: boolean
  canHost: boolean
  onToggled: () => void
}) {
  const [rows, setRows] = useState<MeetingRecording[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [playing, setPlaying] = useState<string | null>(null)

  const pull = useCallback(() => {
    listMeetingRecordings(meetingId).then(setRows).catch((e) => setError(explain(e)))
  }, [meetingId])

  useEffect(() => {
    pull()
    const t = setInterval(pull, POLL_MS)
    return () => clearInterval(t)
  }, [pull])

  const toggle = async () => {
    setBusy(true)
    setError(null)
    try {
      await setMeetingRecording(meetingId, !recordingEnabled)
      onToggled()
      pull()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const play = async (recordingId: string) => {
    setError(null)
    try {
      const { url } = await meetingPlayback(meetingId, recordingId)
      setPlaying(url)
    } catch (e) {
      setError(explain(e))
    }
  }

  return (
    <div className="space-y-3">
      {canHost && (
        <button type="button"
          className={`erp-btn h-9 w-full text-erp-sm ${
            recordingEnabled ? 'erp-btn-ghost text-destructive' : 'erp-btn-primary'}`}
          disabled={busy} onClick={() => void toggle()}>
          {recordingEnabled
            ? <><Square className="size-4" /> Stop recording</>
            : <><CircleDot className="size-4" /> Record this meeting</>}
        </button>
      )}

      {/*
          Present tense only while it is true. `recordingEnabled` outlives the meeting, so
          this said "is being recorded" about a meeting that had finished — see the banner
          in MeetingRoomPage for why a recording indicator must not overstate.
      */}
      {recordingEnabled && live && (
        <Note tone="warn">
          This meeting is being recorded, and everybody in the room is told so. One file is
          kept per person.
        </Note>
      )}
      {recordingEnabled && !live && (
        <Note tone="info">
          This meeting was recorded. The files below are what was kept — one per person.
        </Note>
      )}

      <ErrorStrip message={error} />

      {playing && (
        // Keyed on the URL so swapping recordings reloads the element rather than seeking
        // the old one.
        <video key={playing} src={playing} controls autoPlay
          className="w-full rounded-md bg-neutral-900" />
      )}

      {rows.length === 0 ? (
        <p className="py-4 text-center text-erp-xs text-muted-foreground">
          {recordingEnabled
            ? 'Recording starts when somebody connects.'
            : 'Nothing recorded. Recordings are kept only when a host turns recording on.'}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-2 py-2">
              <span className="min-w-0">
                <span className="block truncate text-erp-sm font-medium">
                  {row.participantName ?? 'Someone'}
                </span>
                <span className="block text-erp-xs text-muted-foreground">
                  {stateLabel(row)}
                  {row.durationSec ? ` · ${fmtDuration(row.durationSec)}` : ''}
                  {row.sizeBytes ? ` · ${fmtSize(row.sizeBytes)}` : ''}
                </span>
                {row.error && (
                  <span className="mt-0.5 flex items-start gap-1 text-erp-xs text-destructive">
                    <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                    {row.error}
                  </span>
                )}
              </span>
              {row.playable && (
                <button type="button" className="erp-btn erp-btn-ghost h-8 shrink-0 text-erp-sm"
                  onClick={() => void play(row.id)}>
                  <Play className="size-4" /> Play
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The server's state, in words — and never claiming more than the server does. */
function stateLabel(row: MeetingRecording): string {
  switch (row.stateCode) {
    case 'PENDING': return 'Starting…'
    case 'RECORDING': return 'Recording'
    case 'READY': return 'Ready'
    case 'FAILED': return 'Failed'
    default: return row.stateCode
  }
}

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m === 0 ? `${s}s` : `${m}m ${String(s).padStart(2, '0')}s`
}

function fmtSize(bytes: number): string {
  const mb = bytes / 1_048_576
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`
}
